import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';
import { formatPKR } from './currencyFormatter';
import { addPdfBrandLogo } from './pdfLogo';

function displayType(product) {
  const t = String(product.product_type ?? product.productType ?? '').toLowerCase();
  if (t === 'stitched') return 'Stitched';
  if (t === 'unstitched') return 'Unstitched';
  return t || '—';
}

function displayUnit(product) {
  const u = String(product.unit_type ?? product.unitType ?? '').toLowerCase();
  if (u === 'guz') return 'Guz';
  if (u === 'piece') return 'Piece';
  return u || '—';
}

function variantLabel(variant) {
  if (!variant) return '—';
  const color = String(variant.color ?? '').trim();
  const size = String(variant.size ?? '').trim();
  if (color && size) return `${color} / ${size}`;
  if (color) return color;
  if (size) return size;
  return variant.sku || '—';
}

function formatQty(qty, unitType) {
  const n = parseFloat(qty) || 0;
  const unit = String(unitType || '').toLowerCase();
  if (unit === 'guz') return n.toFixed(2);
  return String(Math.round(n));
}

function getVariantQuantity(variant, product) {
  if (variant.available_quantity != null && variant.available_quantity !== undefined) {
    return parseFloat(variant.available_quantity) || 0;
  }
  if (variant.display_quantity != null && variant.display_quantity !== undefined) {
    return parseFloat(variant.display_quantity) || 0;
  }
  const fromInventory = (variant.inventories || []).reduce(
    (sum, inv) => sum + (parseFloat(inv.quantity) || 0),
    0
  );
  if (fromInventory > 0 || (variant.inventories || []).length > 0) return fromInventory;
  if (product?.display_quantity != null && product?.display_quantity !== undefined) {
    return parseFloat(product.display_quantity) || 0;
  }
  return 0;
}

function getProductStock(product) {
  if (product.display_quantity !== undefined && product.display_quantity !== null && product.display_quantity !== '') {
    return parseFloat(product.display_quantity) || 0;
  }
  if (product.variants?.length > 0) {
    return product.variants.reduce((sum, variant) => sum + getVariantQuantity(variant, product), 0);
  }
  return parseFloat(product.quantity) || 0;
}

function getUnitCost(product, variant) {
  if (variant) {
    const variantCost = parseFloat(variant.cost_price ?? variant.costPrice);
    if (!Number.isNaN(variantCost) && variantCost > 0) return variantCost;
  }
  const productCost = parseFloat(product.cost_price ?? product.costPrice);
  return Number.isNaN(productCost) ? 0 : productCost;
}

function buildRows(products) {
  const rows = [];
  (Array.isArray(products) ? products : []).forEach((product) => {
    const variants = product.variants || [];
    if (variants.length > 0) {
      variants.forEach((variant) => {
        const qty = getVariantQuantity(variant, product);
        const unitCost = getUnitCost(product, variant);
        rows.push({
          product,
          variant,
          qty,
          unitCost,
          lineValue: qty * unitCost,
        });
      });
      return;
    }

    const qty = getProductStock(product);
    const unitCost = getUnitCost(product, null);
    rows.push({
      product,
      variant: null,
      qty,
      unitCost,
      lineValue: qty * unitCost,
    });
  });
  return rows;
}

/**
 * Product list PDF for the current filtered products table view.
 *
 * @param {object} options
 * @param {string} options.businessName
 * @param {string} [options.branchName]
 * @param {Array<object>} options.products
 * @param {string} [options.searchTerm]
 * @param {string} [options.typeFilter]
 */
export async function downloadProductListPdf(options) {
  const {
    businessName,
    branchName = '',
    products = [],
    searchTerm = '',
    typeFilter = '',
  } = options;

  const list = Array.isArray(products) ? products : [];
  const rows = buildRows(list);
  const totalQuantity = rows.reduce((sum, row) => sum + row.qty, 0);
  const totalCostValue = rows.reduce((sum, row) => sum + row.lineValue, 0);

  const doc = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'a4' });
  const pageW = doc.internal.pageSize.getWidth();
  const margin = 10;
  const { nextY } = await addPdfBrandLogo(doc, { maxWidthMm: 28, startY: 6, gapAfterMm: 3 });
  let y = nextY;

  const trimmedSearch = String(searchTerm || '').trim();
  const title = trimmedSearch ? `Product search results — "${trimmedSearch}"` : 'Product list';

  doc.setFontSize(15);
  doc.setTextColor(17, 24, 39);
  doc.setFont('helvetica', 'bold');
  doc.text(title, margin, y);
  y += 6;

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(10);
  doc.setTextColor(55, 65, 81);
  doc.text(String(businessName || 'Business').trim(), margin, y);
  y += 5;
  doc.text(`Branch: ${String(branchName || '—').trim()}`, margin, y);
  y += 5;

  const filterParts = [];
  if (trimmedSearch) filterParts.push(`Search: ${trimmedSearch}`);
  if (typeFilter) {
    filterParts.push(`Type: ${typeFilter === 'stitched' ? 'Stitched' : typeFilter === 'unstitched' ? 'Unstitched' : typeFilter}`);
  }
  filterParts.push(`Products: ${list.length}`);
  filterParts.push(`Total quantity: ${formatQty(totalQuantity, list[0]?.unit_type ?? list[0]?.unitType)}`);
  filterParts.push(`Total cost value: ${formatPKR(totalCostValue)}`);

  const generated = new Date();
  doc.text(`Generated: ${generated.toLocaleString()}`, margin, y);
  y += 5;
  doc.setFontSize(9);
  doc.setTextColor(100, 116, 139);
  doc.text(filterParts.join(' · '), margin, y);
  y += 6;

  const body = rows.map((row) => {
    const { product, variant, qty, unitCost, lineValue } = row;
    return [
      String(product.product_code ?? product.productCode ?? '—').substring(0, 14),
      String(product.name || '—').substring(0, 30),
      displayType(product),
      String(product.vendor?.name || '—').substring(0, 18),
      variantLabel(variant).substring(0, 20),
      displayUnit(product),
      formatQty(qty, product.unit_type ?? product.unitType),
      unitCost > 0 ? formatPKR(unitCost) : '—',
      lineValue > 0 ? formatPKR(lineValue) : '—',
    ];
  });

  if (body.length === 0) {
    doc.setFontSize(10);
    doc.setTextColor(71, 85, 105);
    doc.text('No products match the current filters.', margin, y + 4);
  } else {
    body.push([
      '',
      '',
      '',
      '',
      '',
      'Totals',
      formatQty(totalQuantity, list[0]?.unit_type ?? list[0]?.unitType),
      '',
      formatPKR(totalCostValue),
    ]);

    autoTable(doc, {
      startY: y,
      head: [['Code', 'Product', 'Type', 'Vendor', 'Variant', 'Unit', 'Quantity', 'Unit cost', 'Total cost']],
      body,
      margin: { left: margin, right: margin },
      tableWidth: pageW - 2 * margin,
      styles: {
        fontSize: 7,
        cellPadding: 1.4,
        textColor: [15, 23, 42],
        lineColor: [200, 200, 200],
        lineWidth: 0.1,
      },
      headStyles: {
        fillColor: [55, 65, 81],
        textColor: [255, 255, 255],
        fontStyle: 'bold',
        fontSize: 7,
      },
      theme: 'grid',
      didParseCell: (data) => {
        const isTotal = data.section === 'body' && data.row.index === body.length - 1;
        if (isTotal) {
          data.cell.styles.fontStyle = 'bold';
          data.cell.styles.fillColor = [248, 250, 252];
        }
      },
      columnStyles: {
        6: { halign: 'right' },
        7: { halign: 'right' },
        8: { halign: 'right' },
      },
    });
  }

  const safeSearch = trimmedSearch
    ? trimmedSearch.replace(/[^\w\s-]/g, '').replace(/\s+/g, '-').slice(0, 24)
    : 'all';
  const dateStr = generated.toISOString().split('T')[0];
  doc.save(`products-list-${safeSearch}-${dateStr}.pdf`);
}
